import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async (uid) => {
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      if (snap.exists()) setUserProfile({ id: snap.id, ...snap.data() });
    } catch {
      // Firebase not configured yet
    }
  };

  const register = async ({ email, password, firstName, lastName, phone, company }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const displayName = `${firstName} ${lastName}`;
    await updateProfile(cred.user, { displayName });

    // Load default WiFi profile ID if one has been set by admin
    let defaultWifiProfileId = null;
    try {
      const wifiSnap = await getDoc(doc(db, 'config', 'wifiProfiles'));
      if (wifiSnap.exists()) defaultWifiProfileId = wifiSnap.data().defaultProfileId || null;
    } catch { /* ignore — not critical */ }

    await setDoc(doc(db, 'users', cred.user.uid), {
      firstName,
      lastName,
      displayName,
      email,
      phone: phone || '',
      company: company || '',
      role: 'member',
      createdAt: serverTimestamp(),
      ...(defaultWifiProfileId ? { wifiProfileId: defaultWifiProfileId } : {}),
    });
    await fetchUserProfile(cred.user.uid);
    return cred.user;
  };

  const login = async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    await fetchUserProfile(cred.user.uid);
    return cred.user;
  };

  const logout = () => signOut(auth);

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) await fetchUserProfile(user.uid);
      else setUserProfile(null);
      setLoading(false);
    });
    return unsub;
  }, []);

  const refreshProfile = () => currentUser && fetchUserProfile(currentUser.uid);

  const value = {
    currentUser,
    userProfile,
    loading,
    register,
    login,
    logout,
    resetPassword,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};
